import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Reply, Heart, Flag, Trash2 } from 'lucide-react';
import CommentForm from './CommentForm';
import { avatarUrlFromPath } from '@/utils/profileUtils';

interface CommentItemProps {
  comment: {
    id: string;
    content: string;
    created_at: string;
    user_id: string;
    attachments?: Array<{
      type: string;
      url: string;
      name: string;
      size: number;
    }>;
    profiles?: {
      username?: string;
      first_name?: string;
      last_name?: string;
      avatar_url?: string;
      avatar_path?: string | null;
      avatar_updated_at?: string | null;
    };
    comment_reactions?: Array<{
      id: string;
      reaction_type: string;
      user_id: string;
    }>;
    replies?: CommentItemProps['comment'][];
  };
  onReply?: (parentId: string, content: string, attachments?: any[]) => Promise<void>;
  onReact?: (commentId: string, type: string) => Promise<void>;
  onRemoveReaction?: (commentId: string, type: string) => Promise<void>;
  onReport?: (commentId: string) => void;
  onDelete?: (commentId: string) => void;
  isReply?: boolean;
  currentUserId?: string;
}

export default function CommentItem({
  comment,
  onReply,
  onReact,
  onRemoveReaction,
  onReport,
  onDelete,
  isReply = false,
  currentUserId
}: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isReplying, setIsReplying] = useState(false);

  const handleReply = async (content: string, attachments?: any[]) => {
    if (!onReply) return;
    
    setIsReplying(true);
    try {
      await onReply(comment.id, content, attachments);
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error posting reply:', error);
    } finally {
      setIsReplying(false);
    }
  };

  const getAuthorName = () => {
    const profile = comment.profiles;
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) return profile.first_name;
    if (profile?.username) return profile.username;
    return 'Anonymous';
  };

  const authorAvatarUrl = avatarUrlFromPath(
    comment.profiles?.avatar_path,
    comment.profiles?.avatar_url,
    comment.profiles?.avatar_updated_at
  );

  const userReactions = comment.comment_reactions?.filter(r => r.user_id === currentUserId) || [];
  const hasLiked = userReactions.some(r => r.reaction_type === 'like');
  const likeCount = comment.comment_reactions?.filter(r => r.reaction_type === 'like').length || 0;
  
  const isOwner = comment.user_id === currentUserId;
  const canDelete = isOwner;
  const canReport = !isOwner && currentUserId;

  return (
    <div className={`space-y-3 ${isReply ? 'ml-8' : ''}`}>
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={authorAvatarUrl} alt={getAuthorName()} />
          <AvatarFallback>
            {getAuthorName().charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 space-y-2">
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-sm">{getAuthorName()}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
                
                {(canDelete || canReport) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <MoreHorizontal size={12} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canDelete && (
                        <DropdownMenuItem onClick={() => onDelete?.(comment.id)}>
                          <Trash2 size={14} className="mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                      {canReport && (
                        <DropdownMenuItem onClick={() => onReport?.(comment.id)}>
                          <Flag size={14} className="mr-2" />
                          Report
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
            
            <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
            
            {/* Image Attachments */}
            {comment.attachments && Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {comment.attachments.map((attachment, index) => (
                  <div key={index} className="relative">
                    <img 
                      src={attachment.url} 
                      alt={attachment.name}
                      className="max-w-48 max-h-48 object-cover rounded border cursor-pointer"
                      onClick={() => window.open(attachment.url, '_blank')}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {onReact && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (hasLiked) {
                    onRemoveReaction?.(comment.id, 'like');
                  } else {
                    onReact(comment.id, 'like');
                  }
                }}
                className={`h-7 px-2 gap-1 ${hasLiked ? 'text-primary' : ''}`}
              >
                <Heart size={14} className={hasLiked ? 'fill-current' : ''} />
                {likeCount > 0 && <span className="text-xs">{likeCount}</span>}
              </Button>
            )}
            
            {onReply && !isReply && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="h-7 px-2 gap-1"
              >
                <Reply size={14} />
                Reply
              </Button>
            )}
            
            {comment.replies && comment.replies.length > 1 && (
              <span className="text-xs text-muted-foreground">
                {comment.replies.length} replies
              </span>
            )}
          </div>
          
          {showReplyForm && onReply && (
            <div className="mt-3">
              <CommentForm
                onSubmit={handleReply}
                placeholder="Write a reply..."
                buttonText="Reply"
                isReply={true}
                autoFocus={true}
              />
            </div>
          )}
        </div>
      </div>
      
      {comment.replies && comment.replies.map((reply) => (
        <CommentItem
          key={reply.id}
          comment={reply}
          onReply={onReply}
          onReact={onReact}
          onRemoveReaction={onRemoveReaction}
          onReport={onReport}
          onDelete={onDelete}
          isReply={true}
          currentUserId={currentUserId}
        />
      ))}
    </div>
  );
}